import type { ReactNode } from 'react';
import { useMemo } from 'react';

import {
  useAutoResolvedSuggestionsSync,
  useCalendarEventsSync,
  useDoBoxRuleDecisionSync,
  useInboxEntriesSync,
  useInboxEntryReplyStateSync,
  useIntegrationAccountsSync,
  useLocalDevicePresenceSync,
  useNotificationsSync,
  useSuggestionTabAlertsSync,
  useTaskGroupsSync,
  useTaskLabelsSync,
  useTaskSubgroupsSync,
  useTeamInvitesSync,
  useTeamSync,
  useUnreadSync,
  useUserCommunicationsSync,
} from './syncs';
import { WebSocketSyncContext } from './WebSocketSyncContext';

interface WebSocketSyncProviderProps {
  children: ReactNode;
}

/**
 * WebSocketSyncProvider
 *
 * Orchestrates WebSocket event synchronization with application stores.
 * Uses modular sync hooks for each entity type.
 *
 * Architecture:
 * - Each entity has its own sync hook in the syncs/ directory
 * - Hooks subscribe to WebSocket events (INSERT, MODIFY, REMOVE, RECONNECT)
 * - Hooks update Zustand stores in real-time
 * - Centralized orchestration, modular implementation
 *
 * Synced Entities:
 * - UserCommunications (emails, messages)
 * - IntegrationAccount (one REMOVE frame per disconnected account, replacing the
 *   per-message REMOVE storm a data-delete purge used to produce)
 * - InboxEntries (grouped inbox items with unread counter management)
 * - InboxEntryReplyState (per-thread unread replies + head UNREAD for DoBox cards)
 * - SuggestionTabAlerts (new default-group suggestions → shared cross-tab unseen
 *   count, tab title, favicon badge, sound)
 * - Notifications (user notifications for banner and notification center)
 * - TeamInvites (pending team invitations)
 * - Team (member rows: join/leave/role changes and team metadata)
 * - CalendarEvent (change signal only — bumps a revision counter that
 *   Calendar's own hook uses to refetch its visible window)
 * - LocalDevicePresence (paired Mac connect/disconnect — writes straight to the
 *   Apollo cache rather than a Zustand store, since the device list is a GraphQL
 *   query and has no store of its own)
 */
export function WebSocketSyncProvider({
  children,
}: WebSocketSyncProviderProps) {
  // Initialize sync hooks for each entity type
  useUserCommunicationsSync();
  useIntegrationAccountsSync();
  useInboxEntriesSync();
  useAutoResolvedSuggestionsSync();
  useInboxEntryReplyStateSync();
  useSuggestionTabAlertsSync();
  useNotificationsSync();
  useTeamInvitesSync();
  useTeamSync();
  useDoBoxRuleDecisionSync();
  useTaskGroupsSync();
  useTaskLabelsSync();
  useTaskSubgroupsSync();
  useUnreadSync();
  useCalendarEventsSync();
  useLocalDevicePresenceSync();

  // Context value - currently minimal, ready for future extensions
  const value = useMemo(() => ({}), []);

  return (
    <WebSocketSyncContext.Provider value={value}>
      {children}
    </WebSocketSyncContext.Provider>
  );
}
